import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner } from '../components/ui';
import { AutoAwesomeIcon } from '../components/ui/Icons';
import { FormErrorDisplay } from '../components/common/ErrorDisplay';
import { toAppError, AppError } from '../types/api';
import { jsonAuthHeaders, AUTH_FETCH_INIT } from '../services/auth/apiAuth';

// Use environment variable for API base URL with fallback for dev proxy
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

const NewAgentProjectPage: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<AppError | null>(null);
  const hasCreated = useRef(false);

  useEffect(() => {
    // Prevent double-creation in React StrictMode
    if (hasCreated.current) return;
    hasCreated.current = true;

    const createAndNavigate = async () => {
      try {
        // task_111: provider-aware headers. Firebase attaches a Bearer token;
        // Studio relies on the gml_sso cookie (credentials: 'include').
        const response = await fetch(`${API_BASE_URL}/projects`, {
          ...AUTH_FETCH_INIT,
          method: 'POST',
          headers: await jsonAuthHeaders(),
          body: JSON.stringify({
            title: 'New Agent Project',
            description: '',
          }),
        });

        if (!response.ok) throw new Error('Failed to create project');

        const data = await response.json();
        navigate(`/projects/${data.id}`, { replace: true });
      } catch (err) {
        setError(toAppError(err, 'api'));
      }
    };

    createAndNavigate();
  }, [navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 mt-8">
        <div className="w-full max-w-md bg-white dark:bg-secondary-800 rounded-lg shadow-sm border border-secondary-200 dark:border-secondary-700 p-8">
          <FormErrorDisplay
            error={error}
            onClose={() => setError(null)}
            context="create_project"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 mt-8">
      <div className="w-full max-w-md bg-white dark:bg-secondary-800 rounded-lg shadow-sm border border-secondary-200 dark:border-secondary-700 p-8">
        <div className="flex items-center gap-3 mb-6">
          <AutoAwesomeIcon className="w-10 h-10 text-primary-600" />
          <h1 className="text-3xl font-bold text-secondary-900 dark:text-secondary-100">
            Design a New AI Agent
          </h1>
        </div>

        <div className="flex flex-col items-center justify-center gap-4 py-8">
          <Spinner size="lg" />
          <p className="text-secondary-600 dark:text-secondary-400">
            Setting up your new agent project...
          </p>
        </div>
      </div>
    </div>
  );
};

export default NewAgentProjectPage;
